<script setup lang="ts">
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import { NavigationBaseProps } from "@/views/blocks/navigation/navigation.interfaces";
import BackgroundComponent from "@/views/blocks/navigation/shared/background/BackgroundComponent.vue";
import PrimaryButton from "@/views/blocks/shared/section/buttons/primary-button/PrimaryButton.vue";
import SecondaryButton from "@/views/blocks/shared/section/buttons/secondary-button/SecondaryButton.vue";
import LogoComponent from "@/views/blocks/navigation/shared/logo/LogoComponent.vue";
import HamburgerButton from "@/views/blocks/navigation/shared/mobile-menu/HamburgerButton.vue";
import MobileMenu from "@/views/blocks/navigation/shared/mobile-menu/MobileMenu.vue";
import NavigationItem from "@/views/blocks/navigation/shared/navigation-items/NavigationItem.vue";
import NavigationItems from "@/views/blocks/navigation/shared/navigation-items/NavigationItems.vue";
import { PageBlock } from "@/views/blocks/PageBlock.interface";
import EditableElement from "@/views/page/EditableElement.vue";
import { computed, ref } from "vue";

const props = defineProps<{
    block: PageBlock<NavigationBaseProps>;
    pageColors: CssVarColors;
    isPreview: boolean;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);

const mobileMenuOpen = ref(false);
</script>

<template>
    <BackgroundComponent
        :id="block.webId"
        :backgroundColor="styleValues.background"
        :isPreview="isPreview"
    >
        <nav
            class="flex gap-4 items-center justify-between lg:container mx-auto lg:gap-12"
            aria-label="Global"
        >
            <div class="lg:flex-1 flex items-center min-w-fit">
                <EditableElement
                    v-if="!dataValues.logo.isHidden"
                    :blockId="block.id"
                    field-name="logo"
                >
                    <LogoComponent v-bind="dataValues.logo.data" />
                </EditableElement>
            </div>
            <NavigationItems class="justify-center">
                <EditableElement
                    v-for="item in dataValues.items.data.items"
                    :blockId="block.id"
                    :field-name="`items_${item.id}`"
                    :key="item.id"
                    :hideable="false"
                >
                    <NavigationItem v-bind="item" :textColor="styleValues.text" />
                </EditableElement>
            </NavigationItems>
            <div class="lg:flex-1 flex items-center justify-end gap-6 min-w-fit">
                <div class="hidden lg:block">
                    <EditableElement
                        v-if="!dataValues.button.isHidden"
                        :blockId="block.id"
                        field-name="button"
                    >
                        <SecondaryButton
                            :label="dataValues.button.data.label"
                            :href="dataValues.button.data.href"
                            :text-color="styleValues.text"
                            :show-arrow="false"
                            class="text-sm"
                        />
                    </EditableElement>
                </div>
                <EditableElement
                    v-if="!dataValues.callToActionButton.isHidden"
                    :blockId="block.id"
                    field-name="callToActionButton"
                >
                    <PrimaryButton
                        :label="dataValues.callToActionButton.data.label"
                        :href="dataValues.callToActionButton.data.href"
                        :background-color="styleValues.brand"
                        :text-color="styleValues.background"
                        class="lg:block px-4 py-2.5 rounded-lg text-sm shadow-xs"
                    />
                </EditableElement>
                <HamburgerButton @openMenu="mobileMenuOpen = true" :textColor="styleValues.text" />
            </div>
        </nav>
        <MobileMenu
            :button="dataValues.button"
            :call-to-action-button="dataValues.callToActionButton"
            :is-open="mobileMenuOpen"
            :items="dataValues.items"
            :logo="dataValues.logo"
            :style-values="styleValues"
            @close="mobileMenuOpen = false"
        />
    </BackgroundComponent>
</template>
